import { useMemo } from 'react'
import styled from 'styled-components'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import useDefaultAirport from 'hooks/useDefaultAirport'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import { unique } from 'lib/array/arrayUtils'
import { useIntl } from 'react-intl'

const Pricing = styled.div`
  display: flex;
  flex-direction: column;
`

interface Props {
  offer: App.Offer | App.OfferSummary
  pkg: App.Package
}

function HeroOfferTileBundlePricing(props: Props) {
  const intl = useIntl()
  const { offer, pkg } = props
  const defaultAirport = useDefaultAirport()

  const flightPrice = defaultAirport?.code ? offer.flightPrices[defaultAirport.code] : undefined

  const {
    totalPrice,
    totalValue,
    saleUnit,
    valueOffPercent,
  } = useOfferPackagePrice(pkg, offer, { flightPrice })

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  if (!flightPrice) {
    return (
      <Pricing>
        <CaptionTextBlock colour="neutral-one" variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon colour="neutral-one" />}>
          {intl.formatMessage({
            id: 'BpMoxNRyvurzLob1K0lS',
            defaultMessage: 'Hotel + Flights packages',
            description: 'Label for hotel and flights bundle packages on hero tile',
          })}
        </CaptionTextBlock>
      </Pricing>
    )
  }

  const showValue = pkg.shouldDisplayValue && totalValue > totalPrice
  const shouldShowRate = (valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE) && !offer.isDiscountPillHidden

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={4}>
      <Group direction="vertical">
        <CaptionTextBlock colour="neutral-one" variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon colour="neutral-one" />}>
          {intl.formatMessage({
            id: 'z87ychBekJMOHRY1J7kS',
            defaultMessage: 'Hotel + Flights',
            description: 'Label for hotel and flights bundle pricing on hero tile',
          })}
        </CaptionTextBlock>
        <PriceRowPriceCaption>{intl.formatMessage({
          id: '1ZwvLNY7GG2TtY05IWQv',
          defaultMessage: '{duration} from',
          description: 'Duration label followed by from for hero hotel bundle pricing',
        }, { duration: offer.tileDurationLabel })}
        </PriceRowPriceCaption>
        <PriceRowPrice
          size="L"
          price={Math.ceil(totalPrice)}
          saleUnit={saleUnit}
        />
        <PriceRowTaxesAndFees />
        {showValue && <PriceRowValueDiscountWithCaption
          size="L"
          originalValue={Math.ceil(totalValue)}
          discountPercentage={shouldShowRate ? valueOffPercent : undefined}
        />}
        <PriceRowCaption>
          {intl.formatMessage({
            id: 'bDAVFDeYsd6-q31Cekaw',
            defaultMessage: '2 adults',
            description: 'Caption indicating pricing is for 2 adults on hero hotel bundle tile',
          })}
        </PriceRowCaption>
      </Group>
      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
        offerLocations={offerLocations}
        vendorName={vendorName}
        packageId={pkg.id}
      />
      <CSSBreakpoint min="desktop">
        <TextButton kind="primary">
          {intl.formatMessage({
            id: 'pQtK86pFFEQUkgintASo',
            defaultMessage: 'View offer',
            description: 'Button to view hotel bundle offer details on hero tile',
          })}
        </TextButton>
      </CSSBreakpoint>
    </Group>
  )
}

export default HeroOfferTileBundlePricing
